import React from 'react'

import some from 'lodash/some'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {signOut} from '../../redux/features/auth'
import {
  closeModal,
  openAddTrackingNumberModal,
  openLoadingModal,
  openSimpleConfirmModal,
  openSimpleContentModal,
} from '../../redux/features/modal'

import {HttpStatusError} from '../../../../commons/src/libs/http-json'
import {captureException, captureIgnoredException} from '../../../../commons/src/services/logging'
import {isClosed} from '../../libs/order'
import {getOrder, patchOrderStatus} from '../../services/api/partner'

import arrowNarrowRightIcon from '../../../../commons/src/assets/icons/arrow-narrow-right.svg'
import flipBackwardIcon from '../../../../commons/src/assets/icons/flip-backward.svg'
import pauseCircleIcon from '../../../../commons/src/assets/icons/pause-circle.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Icon from '../../../../commons/src/components/icon'
import Paragraph from '../../../../commons/src/components/paragraph'
import SelectMenu from '../../../../commons/src/components/select-menu'
import Stack from '../../../../commons/src/components/stack'
import OrderStatusBadge from '../../components/order-status-badge'
import OrderStatusSelectField from '../../components/order-status-select-field'

import {Order, OrderStatusType} from '../../order-types'

class OrderStatusUpdateError extends Error {
  constructor(message?: string) {
    super(message || `Updating Order status failed`)
    this.name = 'OrderStatusUpdateError'
  }
}

const getErrorMessage = (error: unknown) => {
  try {
    if (error && error instanceof HttpStatusError && error.body) {
      return JSON.parse(error.body).error
    }
    return null
  } catch (e) {
    return null
  }
}

export interface OrderStatusOption {
  value: OrderStatusType
  label: React.ReactNode
}

const standardStatusOptions = [
  {
    value: OrderStatusType.BLOCKED,
    label: (
      <ActionBar space="80">
        <Icon source={pauseCircleIcon} />
        Put order on hold
      </ActionBar>
    ),
  },
]

const getNextStatusOption = (type: OrderStatusType, backward = false) => ({
  value: type,
  label: (
    <ActionBar space="80">
      <Icon source={backward ? flipBackwardIcon : arrowNarrowRightIcon} />
      <OrderStatusBadge type={type} />
    </ActionBar>
  ),
})

const getNextStatusOptions = (currentStatus: OrderStatusType) => {
  switch (currentStatus) {
    case OrderStatusType.ORDERED:
      return [
        getNextStatusOption(OrderStatusType.IN_PRODUCTION),
        getNextStatusOption(OrderStatusType.SHIPPED),
        getNextStatusOption(OrderStatusType.RECEIVED),
        ...standardStatusOptions,
      ]
    case OrderStatusType.IN_PRODUCTION:
      return [
        getNextStatusOption(OrderStatusType.SHIPPED),
        getNextStatusOption(OrderStatusType.RECEIVED),
        getNextStatusOption(OrderStatusType.ORDERED, true),
        ...standardStatusOptions,
      ]
    case OrderStatusType.SHIPPED:
      return [
        getNextStatusOption(OrderStatusType.RECEIVED),
        getNextStatusOption(OrderStatusType.ORDERED, true),
        getNextStatusOption(OrderStatusType.IN_PRODUCTION, true),
        ...standardStatusOptions,
      ]
    case OrderStatusType.BLOCKED:
      return [
        getNextStatusOption(OrderStatusType.ORDERED),
        getNextStatusOption(OrderStatusType.IN_PRODUCTION),
        getNextStatusOption(OrderStatusType.SHIPPED),
        getNextStatusOption(OrderStatusType.RECEIVED),
      ]
    default:
      return []
  }
}

export interface Props {
  order: Order
  onOrderUpdate: (updatedOrder: Order) => void
  size?: '90' | '100'
}

const OrderStatusPartial = ({order, onOrderUpdate, size = '100'}: Props) => {
  const dispatch = useAppDispatch()

  const provider = useAppSelector(state => state.core.provider)

  const updateOrderStatus = async (
    nextStatusOption: OrderStatusOption,
    trackingNumber?: string
  ) => {
    await dispatch(openLoadingModal({message: 'Updating order status...'}))
    try {
      await patchOrderStatus(order.orderId, {
        status: nextStatusOption.value,
        trackingNumber,
      })
      const updatedOrder = await getOrder(order.orderId)
      onOrderUpdate(updatedOrder)

      dispatch(closeModal())
    } catch (error) {
      if (error && error instanceof HttpStatusError && error.response.status === 401) {
        captureIgnoredException(error)

        dispatch(closeModal())
        dispatch(signOut())
      } else {
        captureException(
          new OrderStatusUpdateError(getErrorMessage(error) || (error as Error)?.message),
          'default',
          {
            originalError: error,
            orderNumber: order.orderNumber,
            providerId: provider?.id,
            currentStatus: order.printingService.status,
            nextStatus: nextStatusOption.value,
            trackingNumber,
          }
        )
        dispatch(
          openSimpleContentModal({
            title: 'Order Status Update Error',
            content: (
              <Paragraph>
                Oops something went wrong. Please try again and contact us if the problem persists.
              </Paragraph>
            ),
            warning: true,
          })
        )
      }
    }
  }

  const onStatusChange = async (nextStatusOption: OrderStatusOption) => {
    const orderHasTracking =
      order.printingService.trackingNumber || order.printingService.trackingUrl

    if (nextStatusOption.value === OrderStatusType.SHIPPED && !orderHasTracking) {
      if (some(order.additionalCosts, cost => !cost.status.paid)) {
        dispatch(
          openSimpleConfirmModal({
            title: 'Outstanding costs',
            content: (
              <Stack>
                <Paragraph className="u-no-margin-bottom">
                  Are you sure you want to move this order to shipped with outstanding additional
                  costs?
                </Paragraph>
                <Paragraph minor>
                  The customer cannot settle the payment once the order has been invoiced.
                </Paragraph>
              </Stack>
            ),
            onConfirm: () => {
              dispatch(
                openAddTrackingNumberModal({
                  order,
                  onDone: (trackingNumber?: string) => {
                    updateOrderStatus(nextStatusOption, trackingNumber)
                  },
                })
              )
            },
          })
        )
      } else {
        dispatch(
          openAddTrackingNumberModal({
            order,
            onDone: (trackingNumber?: string) => {
              updateOrderStatus(nextStatusOption, trackingNumber)
            },
          })
        )
      }
    } else {
      updateOrderStatus(nextStatusOption)
    }
  }

  if (isClosed(order)) {
    return (
      <OrderStatusBadge
        type={order.printingService.status}
        size={size === '90' ? 's' : 'default'}
      />
    )
  }

  return (
    <OrderStatusSelectField
      value={order.printingService.status}
      menu={<SelectMenu values={getNextStatusOptions(order.printingService.status)} />}
      onChange={onStatusChange}
      size={size}
    />
  )
}

export default OrderStatusPartial
